import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader.jsx';
import { Field } from '@/components/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { useAsignaciones } from './hooks/useAsignaciones.js';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}

function buildChoferLabel(asignacion) {
  const apellido = asignacion.chofer_apellido?.trim();
  const nombre = asignacion.chofer_nombre?.trim();
  return [apellido, nombre].filter(Boolean).join(', ') || '—';
}

function buildVehiculoLabel(asignacion) {
  const detalle = [asignacion.vehiculo_marca, asignacion.vehiculo_modelo].filter(Boolean).join(' ');
  return [asignacion.vehiculo_patente, detalle].filter(Boolean).join(' · ') || '—';
}

const ESTADOS = [
  { value: '', label: 'Todos' },
  { value: 'ACTIVA', label: 'Activa' },
  { value: 'CERRADA', label: 'Cerrada' },
  { value: 'ANULADA', label: 'Anulada' },
];

function getEstadoStyles(estado) {
  switch (estado) {
    case 'ACTIVA':
      return 'bg-[rgba(var(--brand),0.15)] text-[rgb(var(--brand-700))]';
    case 'CERRADA':
      return 'bg-[rgba(var(--muted),0.25)] text-[rgb(var(--fg))]';
    case 'ANULADA':
      return 'bg-[rgba(var(--danger,255_99_99),0.15)] text-[rgb(var(--danger,255_99_99))]';
    default:
      return 'bg-[rgba(var(--panel),0.6)] text-[rgb(var(--muted))]';
  }
}

export default function ListadoAsignacionesFlota() {
  const navigate = useNavigate();
  const { loading, error, success, list, finalizar, loadOpciones } = useAsignaciones();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    chofer_id: '',
    vehiculo_id: '',
    estado: '',
    fecha_desde: '',
    fecha_hasta: '',
  });
  const [opciones, setOpciones] = useState({ choferes: [], vehiculos: [] });

  const loadData = async (nextFilters = filters) => {
    const data = await list({
      chofer_id: nextFilters.chofer_id,
      vehiculo_id: nextFilters.vehiculo_id,
      estado: nextFilters.estado,
      fecha_desde: nextFilters.fecha_desde,
      fecha_hasta: nextFilters.fecha_hasta,
    });
    setRows(data);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const loadOptions = async () => {
      const data = await loadOpciones();
      setOpciones(data);
    };
    loadOptions();
  }, [loadOpciones]);

  const choferesOptions = useMemo(
    () =>
      (opciones.choferes || []).map(chofer => ({
        value: String(chofer.id),
        label: [chofer.apellido, chofer.nombre].filter(Boolean).join(', ') || `Chofer ${chofer.id}`,
      })),
    [opciones.choferes]
  );

  const vehiculosOptions = useMemo(
    () =>
      (opciones.vehiculos || []).map(vehiculo => ({
        value: String(vehiculo.id),
        label: [vehiculo.patente, [vehiculo.marca, vehiculo.modelo].filter(Boolean).join(' ')].filter(Boolean).join(' · '),
      })),
    [opciones.vehiculos]
  );

  const handleFinalizar = async asignacion => {
    const confirmed = window.confirm('¿Querés finalizar esta asignación?');
    if (!confirmed) return;
    await finalizar(asignacion.id, { fecha_hasta: new Date().toISOString().slice(0, 10), estado: 'CERRADA' });
    await loadData();
  };

  const handleSubmitFilters = event => {
    event.preventDefault();
    loadData(filters);
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg),0.95)] pb-16">
      <div className="mx-auto max-w-[1400px] px-4 pb-10 pt-8">
        <PageHeader
          title="Asignaciones de Flota"
          subtitle="Vinculá choferes y vehículos activos con control operativo."
          actions={[
            <button
              key="refresh"
              type="button"
              onClick={loadData}
              className="flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
            >
              Actualizar
            </button>,
            <button
              key="new"
              type="button"
              onClick={() => navigate('/administracion/flota/asignaciones/nueva')}
              className="flex items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
            >
              Nueva asignación
            </button>,
          ]}
        />

        <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.85)] p-6 shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
          <form onSubmit={handleSubmitFilters} className="grid grid-cols-12 gap-4">
            <Field label="Chofer" className="col-span-12 md:col-span-4">
              <select
                value={filters.chofer_id}
                onChange={event => setFilters(prev => ({ ...prev, chofer_id: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Todos los choferes</option>
                {choferesOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Vehículo" className="col-span-12 md:col-span-4">
              <select
                value={filters.vehiculo_id}
                onChange={event => setFilters(prev => ({ ...prev, vehiculo_id: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                <option value="">Todos los vehículos</option>
                {vehiculosOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Estado" className="col-span-12 md:col-span-4">
              <select
                value={filters.estado}
                onChange={event => setFilters(prev => ({ ...prev, estado: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                {ESTADOS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Desde" className="col-span-12 md:col-span-3">
              <input
                type="date"
                value={filters.fecha_desde}
                onChange={event => setFilters(prev => ({ ...prev, fecha_desde: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
            <Field label="Hasta" className="col-span-12 md:col-span-3">
              <input
                type="date"
                value={filters.fecha_hasta}
                onChange={event => setFilters(prev => ({ ...prev, fecha_hasta: event.target.value }))}
                className="mt-1 w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              />
            </Field>
            <div className="col-span-12 md:col-span-3 flex items-end">
              <button
                type="submit"
                className="w-full rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
              >
                Filtrar
              </button>
            </div>
          </form>
        </section>

        {success ? (
          <div className="mt-4 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-4 py-3 text-sm text-[rgb(var(--brand-700))]">
            {success}
          </div>
        ) : null}

        <section className="mt-6 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.8)] shadow-[0_18px_34px_-20px_rgba(var(--shadow),0.45)]">
          <div className="border-b border-[rgb(var(--border))] px-6 py-4 text-sm text-[rgb(var(--muted))]">
            Asignaciones registradas
          </div>
          {error ? (
            <div className="px-6 py-4 text-sm text-[rgb(var(--danger,255_99_99))]">{error}</div>
          ) : null}
          {loading ? (
            <div className="px-6 py-10 text-sm text-[rgb(var(--muted))]">Cargando asignaciones...</div>
          ) : null}
          {!loading && rows.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title="Sin asignaciones"
                description="Todavía no hay asignaciones con esos filtros."
                action={
                  <button
                    type="button"
                    onClick={() => navigate('/administracion/flota/asignaciones/nueva')}
                    className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[rgb(var(--brand-700))] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))]"
                  >
                    Crear asignación
                  </button>
                }
              />
            </div>
          ) : null}
          {!loading && rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[rgba(var(--panel),0.95)] text-xs uppercase tracking-wide text-[rgb(var(--muted))]">
                  <tr>
                    <th className="px-6 py-3 text-left">Chofer</th>
                    <th className="px-6 py-3 text-left">Vehículo</th>
                    <th className="px-6 py-3 text-left">Fecha desde</th>
                    <th className="px-6 py-3 text-left">Fecha hasta</th>
                    <th className="px-6 py-3 text-left">Estado</th>
                    <th className="px-6 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgb(var(--border))]">
                  {rows.map(asignacion => (
                    <tr key={asignacion.id} className="hover:bg-[rgba(var(--muted),0.08)]">
                      <td className="px-6 py-4 font-semibold text-[rgb(var(--fg))]">{buildChoferLabel(asignacion)}</td>
                      <td className="px-6 py-4 text-[rgb(var(--fg))]">{buildVehiculoLabel(asignacion)}</td>
                      <td className="px-6 py-4 text-[rgb(var(--fg))]">{formatDate(asignacion.fecha_desde)}</td>
                      <td className="px-6 py-4 text-[rgb(var(--fg))]">{formatDate(asignacion.fecha_hasta)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center rounded-xl px-2.5 py-1 text-xs font-semibold ${getEstadoStyles(asignacion.estado)}`}>
                          {asignacion.estado}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          {asignacion.estado === 'ACTIVA' ? (
                            <button
                              type="button"
                              onClick={() => handleFinalizar(asignacion)}
                              className="rounded-xl border border-[rgb(var(--border))] px-3 py-1 text-xs text-[rgb(var(--fg))] transition-colors hover:bg-[rgba(var(--muted),0.12)] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                            >
                              Finalizar
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
